import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { breakpoints, spacing } from '@naf/theme';
import { themeLight } from '@lib/nafTheme';
import { Text } from '@naf/text';

const contentAlignment = css`
  --aside: clamp(240px, 25vw, 400px);
  @media (max-width: ${breakpoints.l}) {
    --aside: 0;
  }
  --content-plus-whitespace: calc(100vw - var(--aside));
  --max-width-with-whitespace: min(calc(936px + 2 * 96px), var(--content-plus-whitespace));
  --total-margins: calc((736px - var(--max-width-with-whitespace)) / 2);
  margin-left: max(var(--total-margins) / 2, -96px);
  @media (max-width: ${breakpoints.m}) {
    margin-left: 0;
  }
`;

export const Subheader = styled(Text)`
  margin: 0;
  padding-bottom: ${spacing.space16};
  ${contentAlignment}
`;

export const DataList = styled.div`
  display: flex;
  flex-flow: row wrap;
  ${contentAlignment}
  width: calc(736px - var(--total-margins));
  margin-right: max(var(--total-margins) / 2, -96px);
  margin-bottom: ${spacing.space48};
  @media (max-width: ${breakpoints.l}) {
    margin-bottom: ${spacing.space40};
  }
  @media (max-width: ${breakpoints.m}) {
    margin-right: 0;
    width: 100%;
  }
  @media (max-width: ${breakpoints.s}) {
    flex-flow: column nowrap;
  }
`;

export const Entry = styled.div<{ onClick?: () => void }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${spacing.space4};
  box-sizing: border-box;
  flex: 0 0 50%;
  ${({ onClick }) => onClick && 'cursor: pointer;'}
  @media (max-width: ${breakpoints.s}) {
    & + & {
      border-top: 1px solid ${({ theme }) => theme?.border?.subtle || themeLight.border.subtle};
      padding: ${spacing.space16} 0;
    }
    &:first-of-type {
      padding-bottom: ${spacing.space16};
    }
    &:last-of-type {
      padding-bottom: 0;
    }
  }
  @media (min-width: calc(${breakpoints.s} + 1px)) {
    &:first-of-type,
    &:nth-of-type(2) {
      padding-bottom: ${spacing.space16};
    }
    &:nth-of-type(2n + 1) {
      padding-right: ${spacing.space16};
    }
    &:nth-of-type(2n) {
      border-left: 1px solid ${({ theme }) => theme?.border?.subtle || themeLight.border.subtle};
      padding-left: ${spacing.space16};
    }
    &:nth-of-type(n + 3) {
      border-top: 1px solid ${({ theme }) => theme?.border?.subtle || themeLight.border.subtle};
      padding-top: ${spacing.space16};
      padding-bottom: ${spacing.space16};
    }
    &:last-of-type:nth-of-type(2n),
    &:nth-last-of-type(2):nth-of-type(2n + 1) {
      padding-bottom: 0;
    }
    &:last-of-type:nth-of-type(2n + 1) {
      border-right: 1px solid ${({ theme }) => theme?.border?.subtle || themeLight.border.subtle};
      padding-bottom: 0;
      box-sizing: content-box;
      flex-basis: calc(50% - ${spacing.space16});
    }
    &:nth-last-of-type(2):nth-of-type(2n) {
      border-bottom: 1px solid ${({ theme }) => theme?.border?.subtle || themeLight.border.subtle};
      margin-bottom: -1px;
    }
  }
`;

export const IconContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${spacing.space24};
  height: ${spacing.space24};
  color: ${({ theme }) => theme?.componentColors?.extras?.accent || themeLight.componentColors.extras.accent};
`;
export const Label = styled(Text)`
  flex-grow: 1;
  display: block;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
export const Value = styled(Text)`
  flex-shrink: 0;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
